import Image from "next/image";
import Link from "next/link";

import { YoutubeIcon } from "lucide-react";
import { OrganizationJsonLd } from "next-seo";

import useCalApi from "@/components/hooks/useCalApi";
import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import EmailDemoInput from "@/components/marketing/pieces/EmailDemoInput";
import {
  FACEBOOK_URL,
  INSTAGRAM_URL,
  LINKEDIN_URL,
  PRODUCT_CATEGORIES,
  PRODUCTS,
  TWITTER_URL,
  YOUTUBE_URL,
} from "@/lib/constants";
import { INDUSTRY_SLUGS } from "@/lib/constants/industries";
import { ALTERNATIVES_LINKS, COMPANY, COMPARISON_LINKS, TOOLS } from "@/lib/constants/marketing-items";
import { chunkIntoColumns, getProductHref } from "@/lib/utils/marketing";

import type { ReactNode } from "react";

export default function Footer({ footerCTA }: { footerCTA?: ReactNode }) {
  useCalApi();
  const { calPath } = useCalendarLink();
  const defaultFooterCTA = (
    <div className="flex flex-col items-center gap-6 md:gap-10">
      <h2 className="font-clash font-medium text-3xl md:text-4xl lg:text-6xl leading-[1.1] text-center text-white">
        Ready to scale your business? <br />
        Get started today!
      </h2>
      <EmailDemoInput calPath={calPath} />
    </div>
  );
  return (
    <>
      <OrganizationJsonLd
        type="Corporation"
        logo="https://rebolthq.com/favicon/purp.png"
        name="Rebolt"
        url="https://rebolthq.com"
        sameAs={[FACEBOOK_URL, LINKEDIN_URL, YOUTUBE_URL, TWITTER_URL, INSTAGRAM_URL]}
      />
      <div className="bg-ash-900 w-full px-5 pb-8 md:pb-16 md:px-16 relative isolate overflow-hidden">
        <Image
          src="/marketing-pages/footer-background.png"
          alt="Footer construction"
          width={1920}
          height={400}
          className="absolute inset-0 w-full object-cover select-none -z-10 opacity-50"
        />
        <div className="flex flex-col items-start md:items-center justify-center gap-6 md:gap-10 pt-16 pb-24 md:pt-24 md:pb-36 max-w-6xl mx-auto">
          {footerCTA || defaultFooterCTA}
        </div>

        <div className="flex flex-col md:gap-20 gap-8">
          <div className="flex justify-between lg:flex-row flex-col md:gap-10 gap-6">
            <div className="flex flex-col gap-8 md:gap-12 items-center md:items-start">
              <Image
                src="/rebolt-branding/rebolt-half-white.svg"
                alt="Rebolt Logo"
                width={132}
                height={100}
                className="h-[30px]"
              />
              <div className="flex flex-wrap items-center gap-4">
                <a target="_blank" href={INSTAGRAM_URL} className="text-white border border-white rounded-full p-3">
                  <InstagramIcon className="w-6 h-6" />
                </a>

                <a target="_blank" href={LINKEDIN_URL} className="text-white border border-white rounded-full p-3">
                  <LinkedInIcon className="w-6 h-6" />
                </a>

                <a target="_blank" href={FACEBOOK_URL} className="text-white border border-white rounded-full p-3">
                  <FacebookIcon className="w-6 h-6" />
                </a>

                <a target="_blank" href={TWITTER_URL} className="text-white border border-white rounded-full p-3">
                  <XIcon className="w-6 h-6" />
                </a>

                <a target="_blank" href={YOUTUBE_URL} className="text-white border border-white rounded-full p-3">
                  <YoutubeIcon className="w-6 h-6" />
                </a>
              </div>
            </div>
            <div className="flex gap-6 md:gap-20 text-sm md:text-base leading-normal tracking-snug flex-col md:flex-row ">
              <div className="flex flex-col gap-4">
                <h4 className="font-semibold text-sm leading-normal tracking-[0.05em] uppercase text-rebolt-300">
                  Industries
                </h4>
                <div className="hidden md:flex gap-8">
                  {chunkIntoColumns(INDUSTRY_SLUGS).map((column, columnIndex) => (
                    <ul key={columnIndex} className="flex flex-col gap-4">
                      {column.map(({ slug, label }: { slug: string; label: string }) => (
                        <li key={slug}>
                          <Link
                            href={`/web-design/${slug}`}
                            className="text-white hover:text-rebolt-300 transition-colors duration-200"
                          >
                            {label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ))}
                </div>
                <ul className="flex flex-col gap-4 md:hidden">
                  {INDUSTRY_SLUGS.map(({ slug, label }) => (
                    <li key={slug}>
                      <Link
                        href={`/web-design/${slug}`}
                        className="text-white hover:text-rebolt-300 transition-colors duration-200"
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                  <h4 className="font-semibold text-sm leading-normal tracking-[0.05em] uppercase text-rebolt-300">
                    Products
                  </h4>
                  <ul className="flex flex-col gap-4">
                    <li>
                      <Link
                        href="/ai-voice-assistant"
                        className="text-white hover:text-rebolt-300 transition-colors duration-200"
                      >
                        AI Voice Assistant
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/#marketing-kit"
                        className="text-white hover:text-rebolt-300 transition-colors duration-200"
                      >
                        Marketing Kit
                      </Link>
                    </li>

                    {PRODUCTS.filter((p) => p.category === PRODUCT_CATEGORIES.CRM && !p.isComingSoon).map((product) => (
                      <li key={product.longName}>
                        <Link
                          href={getProductHref(product)}
                          className="text-white hover:text-rebolt-300 transition-colors duration-200"
                        >
                          {product.longName}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col gap-4">
                  <h4 className="font-semibold text-sm leading-normal tracking-[0.05em] uppercase text-rebolt-300">
                    Us Vs Them
                  </h4>
                  <div className="hidden md:flex gap-8">
                    {chunkIntoColumns(COMPARISON_LINKS).map((column, columnIndex) => (
                      <ul key={columnIndex} className="flex flex-col gap-4">
                        {column.map(({ name, slug }: { name: string; slug: string }) => (
                          <li key={name}>
                            <Link
                              href={`/${slug}`}
                              className="text-white hover:text-rebolt-300 transition-colors duration-200"
                            >
                              {name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ))}
                  </div>
                  <ul className="flex flex-col gap-4 md:hidden">
                    {COMPARISON_LINKS.map(({ name, slug }) => (
                      <li key={name}>
                        <Link
                          href={`/${slug}`}
                          className="text-white hover:text-rebolt-300 transition-colors duration-200"
                        >
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col gap-4">
                    <h4 className="font-semibold text-sm leading-normal tracking-[0.05em] uppercase text-rebolt-300">
                      Alternatives
                    </h4>
                    <div className="hidden md:flex gap-8">
                      {chunkIntoColumns(ALTERNATIVES_LINKS).map((column, columnIndex) => (
                        <ul key={columnIndex} className="flex flex-col gap-4">
                          {column.map(({ name, slug }: { name: string; slug: string }) => (
                            <li key={name}>
                              <Link
                                href={`/${slug}`}
                                className="text-white hover:text-rebolt-300 transition-colors duration-200"
                              >
                                {name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ))}
                    </div>
                    <ul className="flex flex-col gap-4 md:hidden">
                      {ALTERNATIVES_LINKS.map(({ name, slug }) => (
                        <li key={name}>
                          <Link
                            href={`/${slug}`}
                            className="text-white hover:text-rebolt-300 transition-colors duration-200"
                          >
                            {name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-4">
                  <h4 className="font-semibold text-sm leading-normal tracking-[0.05em] uppercase text-rebolt-300">
                    Company
                  </h4>
                  <ul className="flex flex-col gap-4">
                    {COMPANY.map(({ name, slug }) => (
                      <li key={name}>
                        <Link href={slug} className="text-white hover:text-rebolt-300 transition-colors duration-200">
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link href="/pricing" className="text-white hover:text-rebolt-300 transition-colors duration-200">
                    Pricing
                  </Link>
                  <Link href="/tutorials" className="text-white hover:text-rebolt-300 transition-colors duration-200">
                    Tutorials
                  </Link>
                  <Link
                    href="/case-studies"
                    className="text-white hover:text-rebolt-300 transition-colors duration-200"
                  >
                    Case Studies
                  </Link>
                </div>

                <div className="flex flex-col gap-4">
                  <h4 className="font-semibold text-sm leading-normal tracking-[0.05em] uppercase text-rebolt-300">
                    Tools
                  </h4>
                  <ul className="flex flex-col gap-4">
                    {TOOLS.map(({ name, slug }) => (
                      <li key={name}>
                        <Link
                          href={`/${slug}`}
                          className="text-white hover:text-rebolt-300 transition-colors duration-200"
                        >
                          {name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="h-px w-full bg-neutral-800 block md:hidden" />

          <div className="flex justify-between gap-4 flex-col sm:flex-row">
            <p className="text-ash-400 ">© {new Date().getFullYear()} RedNine, Inc. All rights reserved.</p>

            <ul className="flex gap-4 items-center">
              <li>
                <Link href="/terms" className="text-ash-400 underline text-sm md:text-base">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link href="/privacy-policy" className="text-ash-400 underline text-sm md:text-base">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12.9 10.1262H16.95L16.5 11.9262H12.9V20.0262H11.1V11.9262H7.5V10.1262H11.1V8.44137C11.1 6.83667 11.2674 6.25437 11.5806 5.66757C11.8875 5.0878 12.3616 4.61368 12.9414 4.30677C13.5282 3.99357 14.1105 3.82617 15.7152 3.82617C16.185 3.82617 16.5972 3.87117 16.95 3.96117V5.62617H15.7152C14.5236 5.62617 14.1609 5.69637 13.791 5.89437C13.5174 6.04017 13.314 6.24357 13.1682 6.51717C12.9702 6.88707 12.9 7.24977 12.9 8.44137V10.1262Z"
        fill="white"
      />
    </svg>
  );
}
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M12 10.1262C11.2839 10.1262 10.5972 10.4106 10.0908 10.917C9.58446 11.4233 9.3 12.1101 9.3 12.8262C9.3 13.5423 9.58446 14.229 10.0908 14.7354C10.5972 15.2417 11.2839 15.5262 12 15.5262C12.7161 15.5262 13.4028 15.2417 13.9092 14.7354C14.4155 14.229 14.7 13.5423 14.7 12.8262C14.7 12.1101 14.4155 11.4233 13.9092 10.917C13.4028 10.4106 12.7161 10.1262 12 10.1262ZM12 8.32617C13.1935 8.32617 14.3381 8.80028 15.182 9.64419C16.0259 10.4881 16.5 11.6327 16.5 12.8262C16.5 14.0196 16.0259 15.1642 15.182 16.0082C14.3381 16.8521 13.1935 17.3262 12 17.3262C10.8065 17.3262 9.66193 16.8521 8.81802 16.0082C7.97411 15.1642 7.5 14.0196 7.5 12.8262C7.5 11.6327 7.97411 10.4881 8.81802 9.64419C9.66193 8.80028 10.8065 8.32617 12 8.32617ZM17.85 8.10117C17.85 8.39954 17.7315 8.68569 17.5205 8.89667C17.3095 9.10765 17.0234 9.22617 16.725 9.22617C16.4266 9.22617 16.1405 9.10765 15.9295 8.89667C15.7185 8.68569 15.6 8.39954 15.6 8.10117C15.6 7.8028 15.7185 7.51665 15.9295 7.30568C16.1405 7.0947 16.4266 6.97617 16.725 6.97617C17.0234 6.97617 17.3095 7.0947 17.5205 7.30568C17.7315 7.51665 17.85 7.8028 17.85 8.10117ZM12 5.62617C9.7734 5.62617 9.4098 5.63247 8.3739 5.67837C7.6683 5.71167 7.1949 5.80617 6.7557 5.97717C6.3651 6.12837 6.0834 6.30927 5.7837 6.60987C5.50201 6.88203 5.28542 7.2143 5.1501 7.58187C4.9791 8.02287 4.8846 8.49537 4.8522 9.20007C4.8054 10.1937 4.8 10.5411 4.8 12.8262C4.8 15.0528 4.8063 15.4164 4.8522 16.4523C4.8855 17.157 4.98 17.6313 5.1501 18.0696C5.3031 18.4611 5.4831 18.7428 5.7819 19.0416C6.0852 19.344 6.3669 19.5249 6.7539 19.6743C7.1985 19.8462 7.6719 19.9416 8.3739 19.974C9.3675 20.0208 9.7149 20.0262 12 20.0262C14.2266 20.0262 14.5902 20.0199 15.6261 19.974C16.3299 19.9407 16.8042 19.8462 17.2434 19.6761C17.6331 19.524 17.9166 19.3431 18.2154 19.0443C18.5187 18.741 18.6996 18.4593 18.849 18.0723C19.02 17.6286 19.1154 17.1543 19.1478 16.4523C19.1946 15.4587 19.2 15.1113 19.2 12.8262C19.2 10.5996 19.1937 10.236 19.1478 9.20007C19.1145 8.49627 19.02 8.02107 18.849 7.58187C18.7134 7.21467 18.4972 6.88254 18.2163 6.60987C17.9443 6.32803 17.612 6.11142 17.2443 5.97627C16.8033 5.80527 16.3299 5.71077 15.6261 5.67837C14.6325 5.63157 14.2851 5.62617 12 5.62617ZM12 3.82617C14.4453 3.82617 14.7504 3.83517 15.7098 3.88017C16.6683 3.92517 17.3208 4.07547 17.895 4.29867C18.489 4.52727 18.9894 4.83687 19.4898 5.33637C19.9474 5.78628 20.3016 6.3305 20.5275 6.93117C20.7498 7.50447 20.901 8.15787 20.946 9.11637C20.9883 10.0758 21 10.3809 21 12.8262C21 15.2715 20.991 15.5766 20.946 16.536C20.901 17.4945 20.7498 18.147 20.5275 18.7212C20.3022 19.3222 19.948 19.8665 19.4898 20.316C19.0398 20.7735 18.4956 21.1275 17.895 21.3537C17.3217 21.576 16.6683 21.7272 15.7098 21.7722C14.7504 21.8145 14.4453 21.8262 12 21.8262C9.5547 21.8262 9.2496 21.8172 8.2902 21.7722C7.3317 21.7272 6.6792 21.576 6.105 21.3537C5.50409 21.1282 4.95977 20.774 4.5102 20.316C4.05247 19.8661 3.69834 19.3219 3.4725 18.7212C3.2493 18.1479 3.099 17.4945 3.054 16.536C3.0117 15.5766 3 15.2715 3 12.8262C3 10.3809 3.009 10.0758 3.054 9.11637C3.099 8.15697 3.2493 7.50537 3.4725 6.93117C3.69771 6.33013 4.05192 5.78576 4.5102 5.33637C4.9599 4.87848 5.50419 4.52433 6.105 4.29867C6.6792 4.07547 7.3308 3.92517 8.2902 3.88017C9.2496 3.83787 9.5547 3.82617 12 3.82617Z"
        fill="white"
      />
    </svg>
  );
}
function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M4.80039 4.72656H19.2004C19.4391 4.72656 19.668 4.82138 19.8368 4.99017C20.0056 5.15895 20.1004 5.38787 20.1004 5.62656V20.0266C20.1004 20.2653 20.0056 20.4942 19.8368 20.663C19.668 20.8317 19.4391 20.9266 19.2004 20.9266H4.80039C4.5617 20.9266 4.33278 20.8317 4.16399 20.663C3.99521 20.4942 3.90039 20.2653 3.90039 20.0266V5.62656C3.90039 5.38787 3.99521 5.15895 4.16399 4.99017C4.33278 4.82138 4.5617 4.72656 4.80039 4.72656ZM5.70039 6.52656V19.1266H18.3004V6.52656H5.70039ZM7.95039 10.1266C7.59235 10.1266 7.24897 9.98433 6.9958 9.73116C6.74262 9.47798 6.60039 9.1346 6.60039 8.77656C6.60039 8.41852 6.74262 8.07514 6.9958 7.82197C7.24897 7.56879 7.59235 7.42656 7.95039 7.42656C8.30843 7.42656 8.65181 7.56879 8.90498 7.82197C9.15816 8.07514 9.30039 8.41852 9.30039 8.77656C9.30039 9.1346 9.15816 9.47798 8.90498 9.73116C8.65181 9.98433 8.30843 10.1266 7.95039 10.1266ZM7.05039 11.0266H8.85039V17.7766H7.05039V11.0266ZM12.0004 11.4136C12.526 10.9051 13.1398 10.5766 13.8004 10.5766C15.6643 10.5766 16.9504 12.0877 16.9504 13.9516V17.7766H15.1504V13.9516C15.1504 13.5338 14.9845 13.1332 14.6891 12.8379C14.3937 12.5425 13.9931 12.3766 13.5754 12.3766C13.1577 12.3766 12.7571 12.5425 12.4617 12.8379C12.1663 13.1332 12.0004 13.5338 12.0004 13.9516V17.7766H10.2004V11.0266H12.0004V11.4136Z"
        fill="white"
      />
    </svg>
  );
}
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M9 5.32617H3.75L9.94565 13.587L4.08746 20.3261H6.07499L10.8662 14.8144L15 20.3262H20.25L13.7938 11.7179L19.3501 5.32617H17.3626L12.8732 10.4905L9 5.32617ZM15.75 18.8262L6.75 6.82617H8.25L17.25 18.8262H15.75Z"
        fill="white"
      />
    </svg>
  );
}
